import { useEffect, useRef } from 'react';

const PARTICLE_COLS = ['61,31,204', '224,123,0', '212,47,92', '8,118,200'];

class Particle {
  constructor(W, H) {
    this.W = W;
    this.H = H;
    this.reset();
  }
  reset() {
    this.x = Math.random() * this.W;
    this.y = Math.random() * this.H;
    this.vx = (Math.random() - 0.5) * 0.22;
    this.vy = (Math.random() - 0.5) * 0.22;
    this.r = Math.random() * 1.3 + 0.4;
    this.life = Math.random();
    this.speed = Math.random() * 0.014 + 0.005;
    this.c = PARTICLE_COLS[Math.floor(Math.random() * PARTICLE_COLS.length)];
  }
  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.life += this.speed;
    if (this.life > 1 || this.x < 0 || this.x > this.W || this.y < 0 || this.y > this.H) {
      this.reset();
    }
  }
  draw(ctx) {
    const a = Math.sin(this.life * Math.PI) * 0.45;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${this.c},${a})`;
    ctx.fill();
  }
}

export function useLandingEffects({ navRef, canvasRef, heroRef, companyInputRef }) {
  const cursorRef = useRef(null);
  const cursorTrailRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const nav = navRef.current;
    const onScroll = () => {
      if (nav) nav.classList.toggle('scrolled', window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navRef]);

  useEffect(() => {
    const revealEls = document.querySelectorAll('.pa-rv');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.1 }
    );
    revealEls.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let W, H, pts = [];

    const resize = () => {
      W = cv.width = window.innerWidth;
      H = cv.height = window.innerHeight;
      pts = [];
      for (let i = 0; i < 95; i++) pts.push(new Particle(W, H));
    };

    const anim = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 105) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(61,31,204,${0.055 * (1 - d / 105)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        pts[i].update();
        pts[i].draw(ctx);
      }
      animRef.current = requestAnimationFrame(anim);
    };

    resize();
    anim();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [canvasRef]);

  useEffect(() => {
    const cur = cursorRef.current;
    const curT = cursorTrailRef.current;
    if (!cur || !curT) return;

    let mx = 0, my = 0, tx = 0, ty = 0;
    let rafId;

    const onMove = e => {
      mx = e.clientX;
      my = e.clientY;
      cur.style.left = `${mx}px`;
      cur.style.top = `${my}px`;
    };

    const loop = () => {
      tx += (mx - tx) * 0.13;
      ty += (my - ty) * 0.13;
      curT.style.left = `${tx}px`;
      curT.style.top = `${ty}px`;
      rafId = requestAnimationFrame(loop);
    };

    const addHov = () => document.body.classList.add('pa-hov');
    const remHov = () => document.body.classList.remove('pa-hov');

    document.addEventListener('mousemove', onMove);
    loop();

    const hovSel = 'a, button, input, select, .pa-pl, .pa-tc, .pa-faqi, .pa-nav-lnk, .pa-nav-cta';
    document.querySelectorAll(hovSel).forEach(el => {
      el.addEventListener('mouseenter', addHov);
      el.addEventListener('mouseleave', remHov);
    });

    return () => {
      document.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(rafId);
      document.body.classList.remove('pa-hov');
    };
  }, []);

  const scrollToHero = () => {
    heroRef.current?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => companyInputRef.current?.focus(), 700);
  };

  const scrollToSection = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return { cursorRef, cursorTrailRef, scrollToHero, scrollToSection };
}
